// The deal flow on the deal's page (TX-US-0043): the path, the words for what a move waits on,
// and what the person looking is offered. Pure, so every stage is tested without a screen. The
// API is the authority (trex/deals/flow_service.py); a control it would refuse is not offered.

import { typeLabel } from '../documents/documentTypes.js';
import { DEAL_EDITOR_ROLES, holdsAny } from '../roles.js';
import { NAMED_PLACE } from './vocabulary.js';

// The screens of the flow render only where the environment has it on (AC19); without it a deal
// is the TX-US-0039 record, its state moved on the form.
export const flowOn = (me) => Boolean(me?.features?.includes('deal_flow'));

export const STAGES = [
  { key: 'PROPOSAL', label: 'Proposal' },
  { key: 'APPROVAL', label: 'Approval' },
  { key: 'CONTRACT', label: 'Contract' },
  { key: 'EXECUTION', label: 'Execution' },
  { key: 'OUTCOME', label: 'Closed' },
];

const FINAL = { CLOSED: 'Closed', REJECTED: 'Rejected', CANCELLED: 'Cancelled' };
const ORDER = STAGES.map((s) => s.key);

export const isFinal = (stage) => stage in FINAL;

export const STAGE_LABELS = {
  ...Object.fromEntries(STAGES.filter((s) => s.key !== 'OUTCOME').map((s) => [s.key, s.label])),
  ...FINAL,
};

// The stage a final deal ended from: the `from` of the change of stage that made it final. A
// rejection is always at Approval; a cancellation may be from any open stage, and only the
// history says which.
export function endedFrom(deal, events = []) {
  if (deal.stage === 'REJECTED') return 'APPROVAL';
  const last = events.findLast(
    (event) => event.event_type === 'STAGE_CHANGED' && event.detail?.to === deal.stage,
  );
  return last?.detail?.from ?? null;
}

// Each stage with its state: done (ticked), current, next, ended - the stage a deal that ended
// without closing ended at - or, for that deal's outcome, rejected. A deal that ended early has
// passed only the stages before the one it ended at.
export function pathOf(deal, ended = null) {
  const closed = deal.stage === 'CLOSED';
  const at = isFinal(deal.stage) && !closed ? ended : deal.stage;
  return STAGES.map((stage) => {
    if (stage.key === 'OUTCOME') {
      if (closed) return { ...stage, state: 'done' };
      if (isFinal(deal.stage)) return { ...stage, label: FINAL[deal.stage], state: 'rejected' };
      return { ...stage, state: 'next' };
    }
    if (closed) return { ...stage, state: 'done' };
    if (at && ORDER.indexOf(stage.key) < ORDER.indexOf(at)) return { ...stage, state: 'done' };
    if (stage.key === at) return { ...stage, state: isFinal(deal.stage) ? 'ended' : 'current' };
    return { ...stage, state: 'next' };
  });
}

// The passed stage the address asks to see (`?stage=`), or null for the current one: only a
// stage the deal has passed holds anything to show.
export function passedStageFrom(searchParams, deal, ended = null) {
  const named = searchParams.get('stage');
  const stage = pathOf(deal, ended).find((s) => s.key === named && s.key !== 'OUTCOME');
  return stage?.state === 'done' ? stage.key : null;
}

const FORWARD = {
  PROPOSAL: { move: 'SUBMIT', label: 'Submit for approval' },
  CONTRACT: { move: 'START_EXECUTION', label: 'Start execution' },
  EXECUTION: { move: 'CLOSE', label: 'Close deal' },
};

// The keys of trex/deals/gates.py that are not a document.
const NEEDED_WORDS = {
  quantity: 'Quantity',
  price: 'Price',
  payment_terms: 'Payment terms',
  delivery_period: 'Delivery period',
  counterparty: 'Counterparty linked to the register',
  counterparty_approved: 'Counterparty approved',
};

const DOCUMENT_PREFIX = 'document:';

// What a move waits on, in words. The named place is labelled by the deal's Incoterm, which the
// caller reads from the vocabulary (vocabulary.js namedPlaceLabel).
export function neededLabel(key, namedPlace = NAMED_PLACE) {
  if (key === 'named_place') return namedPlace;
  if (key.startsWith(DOCUMENT_PREFIX)) return typeLabel(key.slice(DOCUMENT_PREFIX.length));
  return NEEDED_WORDS[key] ?? key;
}

// Whether the next move waits on a document, which is added in the deal's Documents section.
export const waitsOnDocument = (needed) => (needed ?? [])
  .some((item) => !item.present && item.key.startsWith(DOCUMENT_PREFIX));

export const missingLabels = (keys, namedPlace) =>
  (keys ?? []).map((key) => neededLabel(key, namedPlace));

const NONE = Object.freeze({ forward: null, reopen: false, cancel: false, withdraw: false });

// What a deal writer is offered at the deal's stage. Approval is moved on by decisions, so it
// offers nothing forward; whoever submitted may withdraw it.
export function offeredMoves(deal, me) {
  if (isFinal(deal.stage) || !holdsAny(me, DEAL_EDITOR_ROLES)) return NONE;
  const mine = Boolean(me?.email) && deal.approval?.submitted_by === me.email.toLowerCase();
  return {
    forward: FORWARD[deal.stage] ?? null,
    reopen: ['CONTRACT', 'EXECUTION'].includes(deal.stage),
    cancel: true,
    withdraw: deal.stage === 'APPROVAL' && mine,
  };
}

// A deal at Approval or at its end is not edited: the API refuses it with 409.
export const editable = (deal) => deal.stage !== 'APPROVAL' && !isFinal(deal.stage);

export const STEP_LABELS = { COMMERCIAL: 'Commercial approval', RISK: 'Risk approval' };

export const DECISIONS = [
  { decision: 'APPROVED', label: 'Approve', reason: false },
  { decision: 'RETURNED', label: 'Return', reason: true },
  { decision: 'REJECTED', label: 'Reject', reason: true },
];

const DOCUMENT_STAGE_ORDER = [...ORDER.filter((key) => key !== 'OUTCOME'), ...Object.keys(FINAL)];

// The documents grouped by the stage they were added at, in flow order. A document added before
// the flow has no stage, and is grouped last.
export function documentGroups(documents) {
  const groups = DOCUMENT_STAGE_ORDER.map((stage) => ({
    stage,
    label: STAGE_LABELS[stage],
    documents: documents.filter((doc) => doc.stage === stage),
  }));
  groups.push({
    stage: null,
    label: 'Before the flow',
    documents: documents.filter((doc) => !doc.stage),
  });
  return groups.filter((group) => group.documents.length > 0);
}

// What a refused move or decision says. A blocked move lists what it waits on, in the box's
// words; a missing reason is the API's own message; a 409 is a deal that changed under the
// person - or a move its stage no longer offers - and the way on is to reload it.
export function refusalOf(err, namedPlace) {
  if (err.detail?.kind === 'move_blocked') {
    return { message: `Needed first: ${missingLabels(err.detail.missing, namedPlace).join(', ')}.` };
  }
  if (err.status === 409) {
    return { message: `Refused: ${err.message}. Reload the deal to see where it stands.`, stale: true };
  }
  if (err.fields?.reason) return { message: err.fields.reason };
  return { message: err.message };
}

// What the stage alone does not say, as facts beside it: why the deal was returned, rejected or
// cancelled, and that it was entered before its approval could be recorded in T-REX. A
// cancellation's reason is kept on its event, not on the deal.
export function flowNotes(deal, events = []) {
  const notes = [];
  if (deal.return_reason && deal.stage === 'PROPOSAL') {
    notes.push({
      label: 'Returned', text: deal.return_reason, hint: 'The next submission clears this.',
    });
  }
  if (deal.rejection_reason && deal.stage === 'REJECTED') {
    notes.push({ label: 'Rejected', text: deal.rejection_reason });
  }
  if (deal.stage === 'CANCELLED') {
    const reason = events.findLast((event) => event.event_type === 'CANCELLED')?.detail?.reason;
    if (reason) notes.push({ label: 'Cancelled', text: reason });
  }
  if (deal.entered_before_flow) {
    notes.push({ label: 'Entered', text: 'Entered before approval was recorded in T-REX.' });
  }
  return notes;
}
